import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useRestaurantAuth } from './RestaurantAuthContext';
import { useNotifications } from './NotificationContext';
import { useSocket, SocketContext } from './SocketContext';

interface NewOrder {
    orderId: string;
    tableId: string;
    orderNumber: string;
    customerName: string;
    total: number;
    timestamp: string;
}

interface NewOrderContextType {
    newOrderCount: number;
    newOrders: NewOrder[];
    clearNewOrders: () => void;
    markOrderSeen: (orderId: string) => void;
}

const NewOrderContext = createContext<NewOrderContextType | undefined>(undefined);

export const NewOrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    console.log("🍕 NewOrderProvider rendering")
    const [newOrders, setNewOrders] = useState<NewOrder[]>([]);

    // Safely access socket context
    const socketContext = useContext(SocketContext);
    const socket = socketContext ? socketContext.socket : null;

    useEffect(() => {
        if (!socketContext) {
            console.warn("⚠️ NewOrderProvider: SocketContext is missing. Notifications will not work.")
        }
    }, [socketContext])

    const { user } = useRestaurantAuth();
    const { addNotification } = useNotifications();

    const restaurantId = user?.restaurant;

    useEffect(() => {
        if (!socket || !restaurantId) return;

        // Listener for new orders
        const handleNewOrder = (data: { order: NewOrder, timestamp: string }) => {
            console.log('🆕 New order received (Context):', data);
            const order = data.order;

            // Avoid duplicates
            setNewOrders(prev => {
                if (prev.some(o => o.orderId === order.orderId)) return prev;
                return [order, ...prev];
            });

            // Add to global notifications (Top Bar)
            addNotification({
                title: `New Order #${order.orderNumber}`,
                message: `${order.customerName} - $${order.total.toFixed(2)}`,
                type: 'success',
                action: {
                    label: 'View Order',
                    onClick: () => window.location.href = `/admin/orders?id=${order.orderId}`
                }
            }, true); // true = show toast

            // Play notification sound
            playNotificationSound();

            // Show browser notification if permitted
            showBrowserNotification(order);
        };

        socket.on('order_received', handleNewOrder);

        return () => {
            socket.off('order_received', handleNewOrder);
        };
    }, [socket, restaurantId, addNotification]);

    const playNotificationSound = () => {
        try {
            // Create a simple notification sound using Web Audio API
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
            oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
            oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (error) {
            console.log('Could not play notification sound:', error);
        }
    };

    const showBrowserNotification = (order: NewOrder) => {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('New Table Order!', {
                body: `Order #${order.orderNumber} from ${order.customerName} - $${order.total.toFixed(2)}`,
                icon: '/icon-192.png',
            });
        }
    };

    const clearNewOrders = useCallback(() => {
        setNewOrders([]);
    }, []);

    const markOrderSeen = useCallback((orderId: string) => {
        setNewOrders(prev => prev.filter(order => order.orderId !== orderId));
    }, []);

    return (
        <NewOrderContext.Provider
            value={{
                newOrderCount: newOrders.length,
                newOrders,
                clearNewOrders,
                markOrderSeen,
            }}
        >
            {children}
        </NewOrderContext.Provider>
    );
};

export const useNewOrders = () => {
    const context = useContext(NewOrderContext);
    if (context === undefined) {
        throw new Error('useNewOrders must be used within a NewOrderProvider');
    }
    return context;
};
